/** 手机使用要求：记录按字段阅读，金额/薪资对比保留横向表；弹窗表单可换行、主要操作便于触控。只调整显示，不改变数据、权限及审批规则。 */
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "../../components/ui/sonner";
import { Archive, Download, Loader2, Trash2, FolderOpen, Search, X } from "lucide-react";
import { fmtDT } from "../../lib/date";
import { openAuthedPdf } from "../../lib/pdf";
import { HrArchiveTree } from "./HrArchiveTree";

const EMPTY = { by: "", from: "", to: "" };

const Filters = ({ draft, setDraft, zh }) => {
  const T = (a, b) => (zh ? a : b);
  const inp = "h-7 rounded border border-slate-200 bg-white px-2 text-[11px]";
  const dirty = draft.by || draft.from || draft.to;
  return (
    <div className="flex flex-wrap items-center gap-1.5 px-3 py-2 border-b border-slate-100 bg-slate-50/60" data-testid="hrms-archive-filters">
      <Search className="h-3.5 w-3.5 text-slate-400" />
      <input data-testid="hrms-archive-f-by" value={draft.by} onChange={(e) => setDraft((d) => ({ ...d, by: e.target.value }))} placeholder={T("导出人", "Exported by")} className={`${inp} w-28`} />
      <span className="text-[10px] text-slate-400">{T("导出时间", "Exported")}</span>
      <input data-testid="hrms-archive-f-from" type="date" value={draft.from} onChange={(e) => setDraft((d) => ({ ...d, from: e.target.value }))} className={inp} />
      <span className="text-[10px] text-slate-400">–</span>
      <input data-testid="hrms-archive-f-to" type="date" value={draft.to} onChange={(e) => setDraft((d) => ({ ...d, to: e.target.value }))} className={inp} />
      {dirty && <button type="button" data-testid="hrms-archive-f-clear" onClick={() => setDraft(EMPTY)} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 inline-flex items-center gap-0.5 text-[10px] text-slate-500 hover:text-slate-800"><X className="h-3 w-3" />{T("清除", "Clear")}</button>}
    </div>
  );
};

/* 「人事归档」文件夹：导出的履历 PDF 自动按 法人 → 年月 归档，记录导出人与时间；支持导出人 / 时间范围检索、再次下载 */
export const HrArchivePanel = ({ api, zh, canDelete, refreshKey }) => {
  const T = (a, b) => (zh ? a : b);
  const [open, setOpen] = useState(false);
  const [list, setList] = useState(null);
  const [busy, setBusy] = useState("");
  const [draft, setDraft] = useState(EMPTY);
  const [f, setF] = useState(EMPTY);
  const [folder, setFolder] = useState({ branch: "", ym: "" });
  useEffect(() => { const t = setTimeout(() => setF(draft), 350); return () => clearTimeout(t); }, [draft]);
  const load = useCallback(() => api.get("/hrms/archive", { params: f }).then((r) => setList(r.data)).catch(() => setList([])), [api, f]);
  useEffect(() => { load(); }, [load, refreshKey]);
  const rows = (list || []).filter((a) => (!folder.branch || (a.branch || "all") === folder.branch) && (!folder.ym || a.ym === folder.ym));
  const dl = async (a) => {
    setBusy(a.id);
    try { await openAuthedPdf(api, `/files/${a.fileId}/download`, a.filename); } catch { toast.error(T("下载失败", "Download failed")); }
    setBusy("");
  };
  const del = async (a) => {
    if (!window.confirm(T(`删除归档「${a.filename}」？`, `Delete "${a.filename}"?`))) return;
    try { await api.delete(`/hrms/archive/${a.id}`); toast.success(T("已删除", "Deleted")); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || T("删除失败", "Failed")); }
  };
  const kindZh = { history_batch: T("履历合集", "Collection"), history_single: T("单人履历", "Single") };
  return (
    <div className="rounded-xl border border-slate-200 bg-white" data-testid="hrms-archive-panel">
      <button type="button" data-testid="hrms-archive-toggle" onClick={() => setOpen((v) => !v)} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 w-full flex items-center gap-2 px-4 py-2.5 text-left">
        <FolderOpen className="h-4 w-4 text-amber-500" />
        <span className="text-sm font-bold text-slate-700">{T("人事归档", "HR Archive")}</span>
        <span className="text-[11px] text-slate-400">{T("导出的履历 PDF 自动按 法人 → 年月 存入此文件夹（记录导出人与时间）", "Exported history PDFs are filed by entity → month with exporter & time")}</span>
        <span className="ml-auto text-xs text-slate-500" data-testid="hrms-archive-count">{list ? list.length : "…"} {T("份", "files")}</span>
      </button>
      {open && (
        <div className="border-t border-slate-100">
          <Filters draft={draft} setDraft={setDraft} zh={zh} />
          <div className="flex">
            <HrArchiveTree list={list || []} folder={folder} setFolder={setFolder} zh={zh} />
            <div className="flex-1 min-w-0 divide-y divide-slate-50 max-h-72 overflow-y-auto" data-testid="hrms-archive-list" data-count={rows.length}>
              {!list ? <p className="p-3 text-xs text-slate-400"><Loader2 className="h-3 w-3 animate-spin inline" /></p> : rows.length === 0 ? <p className="p-3 text-xs text-slate-400" data-testid="hrms-archive-empty">{T("该文件夹 / 条件下暂无归档文件", "No files match")}</p> : rows.map((a) => (
                <div key={a.id} className="px-4 py-2 flex items-center gap-2 text-xs" data-testid={`hrms-archive-row-${a.id}`} data-branch={a.branch || "all"} data-ym={a.ym || ""}>
                  <Archive className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-slate-700 truncate" title={a.filename}>{a.filename}</p>
                    <p className="text-[10px] text-slate-400">{kindZh[a.kind] || a.kind} · {a.branchName || T("全部法人", "All")} · {a.title} · {a.count} {T("人", "")} · {(a.size / 1024).toFixed(0)} KB · {T("导出人", "by")} {a.exportedBy} · {fmtDT(a.exportedAt)}</p>
                  </div>
                  <button type="button" data-testid={`hrms-archive-dl-${a.id}`} disabled={busy === a.id} onClick={() => dl(a)} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 inline-flex items-center gap-1 text-[11px] font-semibold rounded-full border border-slate-200 px-2.5 py-1 hover:bg-slate-50 disabled:opacity-50">{busy === a.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}{T("下载", "Download")}</button>
                  {canDelete && <button type="button" data-testid={`hrms-archive-del-${a.id}`} onClick={() => del(a)} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-slate-300 hover:text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
